'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  top?: number;
  // If we want the child to become visible before it becomes stuck, we can specify a value, in pixels:
  scrollPadding?: number;
  children: React.ReactNode;
}

function ShownWhenSticky({
  top = 0,
  scrollPadding = 0,
  children,
  style = {},
  ...delegated
}: Props) {
  const [isShown, setIsShown] = React.useState(false);

  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) {
        return;
      }

      const bb = ref.current.getBoundingClientRect();
      setIsShown(bb.top - scrollPadding <= top);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [top, scrollPadding]);

  return (
    <Wrapper
      ref={ref}
      inert={!isShown}
      style={{
        opacity: isShown ? 1 : 0,
        '--top': top + 'px',
        pointerEvents: isShown ? 'auto' : 'none',
        ...style,
      }}
      {...delegated}
    >
      {children}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: sticky;
  top: var(--top);
  transition: opacity 300ms;
`;

export default ShownWhenSticky;
